import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

/**
 * Domiciliary Schedule service — thin wrappers over the manager schedule API.
 * All filtering / resolution happens on the server; these just pass the date
 * and read the lean payload back.
 */
const base = () => `/${getManagerRoutePath()}/domiciliary-schedule`;

const domiciliaryScheduleService = {
  // The whole zone-wise day schedule for a date (YYYY-MM-DD)
  getSchedule: async (date) => {
    const res = await axiosInstance.get(`${base()}${date ? `?date=${date}` : ''}`);
    return res.data;
  },

  // Ranked carer list for assigning one visit on a date
  getAssignableCarers: async (assignmentId, date) => {
    const res = await axiosInstance.get(`${base()}/assignable?assignment_id=${assignmentId}&date=${date}`);
    return res.data;
  },

  // scope: 'today' | 'always'
  assignCarer: async (payload) => {
    const res = await axiosInstance.post(`${base()}/assign`, payload);
    return res.data;
  },

  // Full detail for one visit (tasks + planned/actual time)
  getVisitDetails: async (assignmentId, date, employeeId) => {
    const q = `assignment_id=${assignmentId}&date=${date}${employeeId ? `&employee_id=${employeeId}` : ''}`;
    const res = await axiosInstance.get(`${base()}/visit?${q}`);
    return res.data;
  },

  // Manager signs off an out-of-area location for one end of a visit.
  // phase: 'start' (clock-in) | 'end' (clock-out) | 'both'
  approveVisitLocation: async (visitLogId, phase = 'both') => {
    const res = await axiosInstance.post(`${base()}/visit/approve-location`, {
      visit_log_id: visitLogId,
      phase,
    });
    return res.data;
  },

  // Stop a package from a given day onward
  stopFromDate: async (payload) => {
    const res = await axiosInstance.post(`${base()}/stop-from-date`, payload);
    return res.data;
  },

  // Payroll-ready worked hours (export modal). period: 'day' | 'week'
  getPayroll: async (date, period = 'day', employeeId) => {
    const q = `date=${date}&period=${period}${employeeId ? `&employee_id=${employeeId}` : ''}`;
    const res = await axiosInstance.get(`${base()}/payroll?${q}`);
    return res.data;
  },

  // Zones
  getZones: async () => {
    const res = await axiosInstance.get(`${base()}/zones`);
    return res.data;
  },
  createZone: async (payload) => {
    const res = await axiosInstance.post(`${base()}/zones`, payload);
    return res.data;
  },
  updateZone: async (id, payload) => {
    const res = await axiosInstance.put(`${base()}/zones/${id}`, payload);
    return res.data;
  },
  deleteZone: async (id) => {
    const res = await axiosInstance.delete(`${base()}/zones/${id}`);
    return res.data;
  },

  // Time away + availability
  createTimeAway: async (payload) => {
    const res = await axiosInstance.post(`${base()}/time-away`, payload);
    return res.data;
  },
  // One-off extra availability for a single date
  createAvailability: async (payload) => {
    const res = await axiosInstance.post(`${base()}/availability`, payload);
    return res.data;
  },
  // Remove a time-away booking (restores the carer's availability)
  deleteTimeAway: async (id) => {
    const res = await axiosInstance.delete(`${base()}/time-away/${id}`);
    return res.data;
  },

  // Live attendance status for one carer on a date (clock in/out, break, travel)
  getCarerStatus: async (employeeId, date) => {
    const res = await axiosInstance.get(`${base()}/carer-status?employee_id=${employeeId}&date=${date}`);
    return res.data;
  },

  // Record / correct actual worked time for a visit (review resolution)
  recordVisitHours: async (payload) => {
    const res = await axiosInstance.post(`${base()}/review`, payload);
    return res.data;
  },

  // Publish the drafted schedule (notify carers) — called after the draft
  // queue has been replayed to the API.
  publishSchedule: async () => {
    const res = await axiosInstance.post(`${base()}/publish`);
    return res.data;
  },
};

export default domiciliaryScheduleService;
